// TrackingPage.tsx — Public shipment tracker (no login required)
// URL: app.blacktopsystem.com/track?token=<uuid>
// Dispatcher shares this link with shippers/consignees.

import React, { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';

export default function TrackingPage() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setError('No tracking token in URL.'); setLoading(false); return; }
    fetch(`${SUPABASE_URL}/functions/v1/track?token=${token}`, {
      headers: { 'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '' },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Unable to load tracking info. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const s = {
    root: { minHeight:'100vh', background:'#000', color:'#fff', fontFamily:'monospace', padding:'24px 16px', maxWidth:'600px', margin:'0 auto' } as React.CSSProperties,
    logo: { color:'#32CD32', fontSize:'18px', fontWeight:700, letterSpacing:'4px', marginBottom:'4px' } as React.CSSProperties,
    sub:  { color:'#555', fontSize:'10px', letterSpacing:'2px', marginBottom:'32px' } as React.CSSProperties,
    card: { background:'#050505', border:'1px solid #1a1a1a', padding:'20px', marginBottom:'16px' } as React.CSSProperties,
    label:{ color:'#555', fontSize:'10px', letterSpacing:'1px', marginBottom:'4px' } as React.CSSProperties,
    val:  { color:'#fff', fontSize:'16px', fontWeight:700 } as React.CSSProperties,
    sm:   { color:'#aaa', fontSize:'13px' } as React.CSSProperties,
  };

  const stepStyle = (done: boolean, current: boolean): React.CSSProperties => ({
    display:'flex', alignItems:'center', gap:'12px', padding:'10px 0',
    borderBottom:'1px solid #0a0a0a', opacity: done || current ? 1 : 0.3,
  });

  const dotStyle = (done: boolean, current: boolean): React.CSSProperties => ({
    width:'20px', height:'20px', borderRadius:'50%', flexShrink:0,
    background: done ? '#32CD32' : current ? '#32CD32' : '#111',
    border: current ? '2px solid #fff' : done ? 'none' : '1px solid #333',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:'10px', color: done ? '#000' : '#555',
  });

  if (loading) return (
    <div style={{ ...s.root, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}>
      <div style={s.logo}>BLACKTOP</div>
      <div style={{ color:'#555', marginTop:'24px', fontSize:'12px' }}>Loading shipment info...</div>
    </div>
  );

  if (error) return (
    <div style={{ ...s.root, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}>
      <div style={s.logo}>BLACKTOP</div>
      <div style={{ color:'#cc3333', marginTop:'24px', fontSize:'13px' }}>{error}</div>
    </div>
  );

  return (
    <div style={s.root}>
      <div style={s.logo}>BLACKTOP</div>
      <div style={s.sub}>SHIPMENT TRACKER</div>

      {/* Status banner */}
      <div style={{ ...s.card, border:`1px solid ${data.is_delivered ? '#32CD32' : '#333'}`, marginBottom:'24px' }}>
        <div style={s.label}>LOAD #{data.load_number}</div>
        <div style={{ ...s.val, color:'#32CD32', fontSize:'20px', marginBottom:'8px' }}>{data.status}</div>
        {data.driver_location && <div style={s.sm}>Driver currently in {data.driver_location}</div>}
        {data.delivered_at && <div style={{ color:'#555', fontSize:'11px', marginTop:'4px' }}>Delivered {data.delivered_at}</div>}
      </div>

      {/* Route */}
      <div style={s.card}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'12px', alignItems:'center' }}>
          <div>
            <div style={s.label}>ORIGIN</div>
            <div style={{ color:'#fff', fontWeight:700 }}>{data.route.origin}</div>
            <div style={{ color:'#555', fontSize:'11px', marginTop:'2px' }}>{data.route.pickup_date}</div>
          </div>
          <div style={{ color:'#32CD32', fontSize:'20px' }}>→</div>
          <div style={{ textAlign:'right' }}>
            <div style={s.label}>DESTINATION</div>
            <div style={{ color:'#fff', fontWeight:700 }}>{data.route.destination}</div>
            <div style={{ color:'#555', fontSize:'11px', marginTop:'2px' }}>{data.route.delivery_date}</div>
          </div>
        </div>
      </div>

      {/* Pipeline */}
      <div style={s.card}>
        <div style={{ ...s.label, marginBottom:'12px' }}>SHIPMENT PROGRESS</div>
        {data.pipeline.map((step: any, i: number) => (
          <div key={i} style={stepStyle(step.completed, step.current)}>
            <div style={dotStyle(step.completed, step.current)}>
              {step.completed ? '✓' : ''}
            </div>
            <div style={{ color: step.current ? '#32CD32' : step.completed ? '#aaa' : '#333', fontWeight: step.current ? 700 : 400, fontSize:'13px' }}>
              {step.label}
              {step.current && <span style={{ color:'#32CD32', marginLeft:'8px', fontSize:'10px' }}>← NOW</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ color:'#333', fontSize:'10px', textAlign:'center', marginTop:'24px' }}>
        Powered by Blacktop TMS · app.blacktopsystem.com
      </div>
    </div>
  );
}
